import type { Breadcrumb, Event, StackFrame } from "@sentry/node"
import { sanitizeLogRecord, sanitizeLogValue } from "@aix/jsutils/src/axiom-events/sanitize"

const DIAGNOSTIC_KEYS = new Set([
  "requestId",
  "userId",
  "teamId",
  "accountId",
  "videoId",
  "jobId",
  "uniqueId",
  "appId",
  "appName",
  "environment",
  "executionOrigin",
  "serverBuild",
  "clientBuild",
  "requestSource",
  "requestMethod",
  "router",
  "procedure",
  "provider",
  "operation",
  "status",
  "statusCode",
  "applicationErrorCode",
  "applicationErrorClassification",
  "applicationErrorRecoveryAction",
  "turnstileRejection",
  "route",
  "durationMs",
  "attempt",
  "count",
])
const OMITTED = "[Private diagnostic details omitted]"
const MAX_DIAGNOSTIC_TEXT = 500
const MAX_BREADCRUMBS = 100
const LEVELS = ["fatal", "error", "warning", "log", "info", "debug"]
const IDENTIFIER = /^[\w.$:<>()[\] -]{1,200}$/
const ERROR_TYPE = /^[\w.$]{1,100}$/
// Axiom identity is allowlist-only: `name`, `constructor` and `code` are all assignable, so any
// syntax check would still pass a one-word username. Unlisted values are omitted; Sentry has them.
const KNOWN_ERROR_TYPES = new Set([
  "AbortError",
  "AggregateError",
  "AxiosError",
  "DOMException",
  "Error",
  "EvalError",
  "FetchError",
  "PrismaClientInitializationError",
  "PrismaClientKnownRequestError",
  "PrismaClientRustPanicError",
  "PrismaClientUnknownRequestError",
  "PrismaClientValidationError",
  "RangeError",
  "ReferenceError",
  "SyntaxError",
  "TimeoutError",
  "TRPCError",
  "TypeError",
  "URIError",
  "ZodError",
])
const PRISMA_ERROR_CODE = /^P\d{4}$/
const KNOWN_ERROR_CODES = new Set([
  // Node, axios and undici
  "ERR_BAD_REQUEST",
  "ERR_BAD_RESPONSE",
  "ERR_CANCELED",
  "ERR_INVALID_ARG_TYPE",
  "ERR_INVALID_URL",
  "ERR_NETWORK",
  "ERR_STREAM_PREMATURE_CLOSE",
  "UND_ERR_CONNECT_TIMEOUT",
  "UND_ERR_HEADERS_TIMEOUT",
  "UND_ERR_SOCKET",
  // errno (network + fs)
  "EACCES",
  "EADDRINUSE",
  "EAI_AGAIN",
  "ECONNABORTED",
  "ECONNREFUSED",
  "ECONNRESET",
  "EEXIST",
  "EHOSTUNREACH",
  "EMFILE",
  "ENETUNREACH",
  "ENOENT",
  "ENOSPC",
  "ENOTFOUND",
  "EPERM",
  "EPIPE",
  "ETIMEDOUT",
  // TRPCError codes
  "BAD_GATEWAY",
  "BAD_REQUEST",
  "CLIENT_CLOSED_REQUEST",
  "CONFLICT",
  "FORBIDDEN",
  "GATEWAY_TIMEOUT",
  "INTERNAL_SERVER_ERROR",
  "METHOD_NOT_SUPPORTED",
  "NOT_FOUND",
  "NOT_IMPLEMENTED",
  "PARSE_ERROR",
  "PAYLOAD_TOO_LARGE",
  "PAYMENT_REQUIRED",
  "PRECONDITION_FAILED",
  "SERVICE_UNAVAILABLE",
  "TIMEOUT",
  "TOO_MANY_REQUESTS",
  "UNAUTHORIZED",
  "UNPROCESSABLE_CONTENT",
  "UNSUPPORTED_MEDIA_TYPE",
])
// gRPC, HTTP and MySQL codes; larger integers could be phone/account numbers.
const MAX_NUMERIC_ERROR_CODE = 9_999
const MAX_PROTOTYPE_DEPTH = 20
const SOURCE_PATH = /^[\w.~:/@+-]{1,500}$/
type DebugImage = NonNullable<NonNullable<Event["debug_meta"]>["images"]>[number]
const URL_USERINFO = /^([a-z][a-z0-9+.-]*:\/\/)[^/?#@]*@/i
const DEBUG_ID = /^[a-f0-9]{8}-?[a-f0-9]{4}-?[a-f0-9]{4}-?[a-f0-9]{4}-?[a-f0-9]{12}$/i

/**
 * Error text keeps its shape but loses likely identifiers. Heuristic backstop; Sentry server-side
 * scrubbing (`api-sentry scrubbing`) is the second layer.
 */
export function scrubDiagnosticText(input: unknown): string | undefined {
  if (typeof input !== "string" || !input) return undefined
  // URLs first: userinfo (`user:pass@host`) would otherwise be half-matched by the email rule.
  const text = sanitizeLogValue(
    input
      .slice(0, MAX_DIAGNOSTIC_TEXT * 4)
      .replace(
        /\b[a-z][a-z0-9+.-]*:\/\/[^\s"'<>]+/gi,
        (url) => `${url.replace(URL_USERINFO, "$1").split(/[/?#]/).slice(0, 3).join("/")}/[path]`,
      ),
  )
  if (typeof text !== "string") return OMITTED
  const scrubbed = text
    .replace(/\b(?:\d{1,3}\.){3}\d{1,3}\b/g, "[ip]")
    .replace(/\b[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\b/gi, "[uuid]")
    .replace(/\b(?=[A-Za-z_]*\d)[A-Za-z0-9_]{20,}\b/g, "[token]")
    .replace(/\+?\d[\d ().-]{7,}\d/g, "[number]")
  return scrubbed.length > MAX_DIAGNOSTIC_TEXT ? `${scrubbed.slice(0, MAX_DIAGNOSTIC_TEXT)}…` : scrubbed
}

/** Error diagnostics use an allowlist: arbitrary text cannot be proven free of PII. */
export function projectErrorMetadata(input: object): Record<string, string | number | boolean> {
  const output: Record<string, string | number | boolean> = Object.create(null)
  for (const [key, value] of Object.entries(sanitizeLogRecord(input))) {
    if (DIAGNOSTIC_KEYS.has(key) && ["string", "number", "boolean"].includes(typeof value)) {
      output[key] = value as string | number | boolean
    }
  }
  return output
}

function ownData(input: unknown, key: string): unknown {
  if (!input || typeof input !== "object") return undefined
  const descriptor = Object.getOwnPropertyDescriptor(input, key)
  return descriptor && "value" in descriptor ? descriptor.value : undefined
}

/** Data property lookup along the prototype chain (`constructor` lives on the class prototype). */
function dataProperty(input: unknown, key: string): unknown {
  let target = input
  for (
    let depth = 0;
    target && (typeof target === "object" || typeof target === "function") && depth < MAX_PROTOTYPE_DEPTH;
    depth += 1
  ) {
    const descriptor = Object.getOwnPropertyDescriptor(target, key)
    if (descriptor) return "value" in descriptor ? descriptor.value : undefined
    target = Object.getPrototypeOf(target)
  }
  return undefined
}

function isKnownErrorCode(code: unknown): code is string | number {
  if (typeof code === "number") return Number.isSafeInteger(code) && code >= 0 && code <= MAX_NUMERIC_ERROR_CODE
  return typeof code === "string" && (PRISMA_ERROR_CODE.test(code) || KNOWN_ERROR_CODES.has(code))
}

/**
 * Axiom-side identity of a reported error: class name + machine code, never message or stack.
 * Axiom has no server-side scrubbing behind `scrubDiagnosticText`, so both fields are allowlisted values.
 */
export function projectErrorIdentity(error: unknown): { errorType?: string; errorCode?: string | number } {
  try {
    const errorType = [dataProperty(error, "name"), dataProperty(dataProperty(error, "constructor"), "name")].find(
      (name): name is string => typeof name === "string" && KNOWN_ERROR_TYPES.has(name),
    )
    const code = dataProperty(error, "code")
    const errorCode = isKnownErrorCode(code) ? code : undefined
    return {
      ...(errorType === undefined ? {} : { errorType }),
      ...(errorCode === undefined ? {} : { errorCode }),
    }
  } catch {
    // Revoked proxies / reflection traps: correlation is best-effort, never a raw fallback.
    return {}
  }
}

/** Source locations keep their path (needed for source maps) but never query strings or fragments. */
function sourcePath(input: unknown): string | undefined {
  if (typeof input !== "string") return undefined
  const path = input
    .split(/[?#]/)[0]
    ?.replace(URL_USERINFO, "$1")
    // Hide the username but stay absolute: Sentry matches abs_path to debug_meta code_file
    // for source maps, and treats "~/" as a URL wildcard.
    .replace(/^\/(home|Users)\/[^/]+\//, "/$1/user/")
  return path && SOURCE_PATH.test(path) ? path : undefined
}

function projectDebugImages(input: unknown): DebugImage[] {
  const images: DebugImage[] = []
  const raw = ownData(input, "images")
  if (!Array.isArray(raw)) return images
  for (let index = 0; index < Math.min(raw.length, 200); index += 1) {
    const image = ownData(raw, String(index))
    const debugId = ownData(image, "debug_id")
    const codeFile = sourcePath(ownData(image, "code_file"))
    if (ownData(image, "type") === "sourcemap" && typeof debugId === "string" && DEBUG_ID.test(debugId) && codeFile) {
      images.push({ type: "sourcemap", debug_id: debugId, code_file: codeFile })
    }
  }
  return images
}

/** Breadcrumbs keep the timeline shape (when/what kind), never messages or data payloads. */
function projectBreadcrumbs(input: unknown): Breadcrumb[] {
  if (!Array.isArray(input)) return []
  const breadcrumbs: Breadcrumb[] = []
  for (let index = Math.max(0, input.length - MAX_BREADCRUMBS); index < input.length; index += 1) {
    const raw = ownData(input, String(index))
    const category = ownData(raw, "category")
    const type = ownData(raw, "type")
    const level = ownData(raw, "level")
    const timestamp = ownData(raw, "timestamp")
    if (typeof timestamp !== "number") continue
    breadcrumbs.push({
      category: typeof category === "string" && IDENTIFIER.test(category) ? category : undefined,
      type: typeof type === "string" && IDENTIFIER.test(type) ? type : undefined,
      level: typeof level === "string" && LEVELS.includes(level) ? (level as Breadcrumb["level"]) : undefined,
      timestamp,
    })
  }
  return breadcrumbs
}

function projectFrames(input: unknown): StackFrame[] {
  if (!Array.isArray(input)) return []
  const frames: StackFrame[] = []
  for (let index = 0; index < Math.min(input.length, 50); index += 1) {
    const raw = ownData(input, String(index))
    if (!raw || typeof raw !== "object") continue
    const frame = sanitizeLogRecord(raw)
    frames.push({
      filename: sourcePath(ownData(raw, "filename")),
      abs_path: sourcePath(ownData(raw, "abs_path")),
      module: typeof frame.module === "string" && IDENTIFIER.test(frame.module) ? frame.module : undefined,
      function: typeof frame.function === "string" && IDENTIFIER.test(frame.function) ? frame.function : undefined,
      lineno: typeof frame.lineno === "number" ? frame.lineno : undefined,
      colno: typeof frame.colno === "number" ? frame.colno : undefined,
      in_app: typeof frame.in_app === "boolean" ? frame.in_app : undefined,
    })
  }
  return frames
}

/** Runs after SDK serialization. Rebuild, rather than spread, the export payload. */
export function sanitizeSentryEvent<T extends Event>(event: T): T | null {
  try {
    const snapshot = sanitizeLogRecord(event)
    const tags = projectErrorMetadata(snapshot.tags && typeof snapshot.tags === "object" ? snapshot.tags : {})
    const extra = projectErrorMetadata(snapshot.extra && typeof snapshot.extra === "object" ? snapshot.extra : {})
    const user = ownData(event, "user")
    const userId = projectErrorMetadata({ userId: ownData(user, "id") }).userId
    const rawTrace = ownData(ownData(event, "contexts"), "trace")
    const trace = rawTrace && typeof rawTrace === "object" ? sanitizeLogRecord(rawTrace) : {}
    const traceId =
      typeof trace.trace_id === "string" && /^[a-f0-9]{32}$/i.test(trace.trace_id) ? trace.trace_id : undefined
    const spanId =
      typeof trace.span_id === "string" && /^[a-f0-9]{16}$/i.test(trace.span_id) ? trace.span_id : undefined
    const values = ownData(ownData(event, "exception"), "values")
    const exceptions: NonNullable<Event["exception"]>["values"] = []
    if (Array.isArray(values)) {
      for (let index = 0; index < Math.min(values.length, 20); index += 1) {
        const value = ownData(values, String(index))
        const handled = ownData(ownData(value, "mechanism"), "handled")
        const type = ownData(value, "type")
        exceptions.push({
          type: typeof type === "string" && ERROR_TYPE.test(type) ? type : "Error",
          value: scrubDiagnosticText(ownData(value, "value")) ?? OMITTED,
          mechanism: typeof handled === "boolean" ? { type: "generic", handled } : undefined,
          stacktrace: { frames: projectFrames(ownData(ownData(value, "stacktrace"), "frames")) },
        })
      }
    }
    const debugImages = projectDebugImages(ownData(event, "debug_meta"))
    const breadcrumbs = projectBreadcrumbs(ownData(event, "breadcrumbs"))
    const message = ownData(event, "message")
    const logMessage =
      ownData(ownData(event, "logentry"), "formatted") ?? ownData(ownData(event, "logentry"), "message")
    const safe: Event = {
      event_id: typeof snapshot.event_id === "string" ? snapshot.event_id : undefined,
      timestamp: typeof snapshot.timestamp === "number" ? snapshot.timestamp : undefined,
      start_timestamp: typeof snapshot.start_timestamp === "number" ? snapshot.start_timestamp : undefined,
      level: LEVELS.includes(String(snapshot.level)) ? (snapshot.level as Event["level"]) : undefined,
      type: snapshot.type === "transaction" ? "transaction" : undefined,
      platform: typeof snapshot.platform === "string" ? snapshot.platform : undefined,
      environment: typeof snapshot.environment === "string" ? snapshot.environment : undefined,
      release: typeof snapshot.release === "string" ? snapshot.release : undefined,
      message: message || logMessage ? (scrubDiagnosticText(message || logMessage) ?? OMITTED) : undefined,
      exception: exceptions.length ? { values: exceptions } : undefined,
      breadcrumbs: breadcrumbs.length ? breadcrumbs : undefined,
      debug_meta: debugImages.length ? { images: debugImages } : undefined,
      tags,
      extra,
      user: typeof userId === "string" ? { id: userId } : undefined,
      contexts: traceId && spanId ? { trace: { trace_id: traceId, span_id: spanId } } : undefined,
      fingerprint: typeof tags.uniqueId === "string" ? [tags.uniqueId, "{{ default }}"] : undefined,
      transaction: snapshot.type === "transaction" ? "[Private transaction name omitted]" : undefined,
    }
    // SDK hook preserves the event discriminator while rebuilding its data at this boundary.
    return safe as T
  } catch {
    // Never fall back to exporting an unreadable raw event.
    return null
  }
}
